import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { UserFacingError, getApiErrorMessage } from '@/shared/api/getApiErrorMessage'
import {
  deleteClientEmployeePayRate,
  fetchClientEmployeePayRates,
  fetchShiftRates,
  fetchSitePayRates,
  resolvePayRates,
  updateSitePayRates,
  upsertClientEmployeePayRate,
} from '../api/payRateApi'
import type {
  UpdateSitePayRatesRequest,
  UpsertClientEmployeePayRateRequest,
} from './types'

export const payRateKeys = {
  all: ['pay-rates'] as const,
  site: (siteId: number) => [...payRateKeys.all, 'site', siteId] as const,
  clientEmployees: (clientId: number) =>
    [...payRateKeys.all, 'client-employees', clientId] as const,
  resolve: (siteId: number, employeeUserIds: number[]) =>
    [...payRateKeys.all, 'resolve', siteId, employeeUserIds] as const,
  shift: (shiftId: number) => [...payRateKeys.all, 'shift', shiftId] as const,
}

export class PayRateRequestError extends UserFacingError {
  readonly status: number | undefined

  constructor(message: string, status: number | undefined) {
    super(message)
    this.name = 'PayRateRequestError'
    this.status = status
  }
}

const asUserFacing = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request()
  } catch (error) {
    throw new PayRateRequestError(
      await getApiErrorMessage(error),
      axios.isAxiosError(error) ? error.response?.status : undefined,
    )
  }
}

export const useResolvedPayRates = (
  siteId: number,
  employeeUserIds: number[],
  enabled: boolean = true,
) => {
  const sortedIds = [...employeeUserIds].sort((a, b) => a - b)

  return useQuery({
    queryKey: payRateKeys.resolve(siteId, sortedIds),
    queryFn: () => asUserFacing(() => resolvePayRates(siteId, sortedIds)),
    enabled: enabled && siteId > 0 && sortedIds.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

export const useShiftRates = (shiftId: number, enabled: boolean = true) =>
  useQuery({
    queryKey: payRateKeys.shift(shiftId),
    queryFn: () => asUserFacing(() => fetchShiftRates(shiftId)),
    enabled: enabled && shiftId > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

export const useSitePayRates = (siteId: number, enabled: boolean = true) =>
  useQuery({
    queryKey: payRateKeys.site(siteId),
    queryFn: () => asUserFacing(() => fetchSitePayRates(siteId)),
    enabled: enabled && siteId > 0,
  })

export const useClientEmployeePayRates = (clientId: number, enabled: boolean = true) =>
  useQuery({
    queryKey: payRateKeys.clientEmployees(clientId),
    queryFn: () => asUserFacing(() => fetchClientEmployeePayRates(clientId)),
    enabled: enabled && clientId > 0,
  })

export const useUpdateSitePayRates = (siteId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateSitePayRatesRequest) => updateSitePayRates(siteId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payRateKeys.all })
    },
  })
}

export const useUpsertClientEmployeePayRate = (clientId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      employeeUserId,
      request,
    }: {
      employeeUserId: number
      request: UpsertClientEmployeePayRateRequest
    }) => upsertClientEmployeePayRate(clientId, employeeUserId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payRateKeys.all })
    },
  })
}

export const useDeleteClientEmployeePayRate = (clientId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      employeeUserId,
      applyFrom,
    }: {
      employeeUserId: number
      applyFrom: string | null
    }) => deleteClientEmployeePayRate(clientId, employeeUserId, applyFrom),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payRateKeys.all })
    },
  })
}
